// @ts-nocheck
// Location-based Product Availability System
// - Requires snippet: snippets/location-selector-modal.liquid included in theme layout
// - Data files: assets/delivery-zones.json, and province/districts loaded from /Turkey.json
//   Optionally, if you later add assets/turkey-locations.json (simplified format), this script will use it.
//
// Integration instructions (summary):
// 1) Add in layout/theme.liquid, right after <body>:
//    {% render 'location-selector-modal' %}
// 2) Before </body>, include:
//    <script src="{{ 'location-filter.js' | asset_url }}" defer></script>
// 3) Ensure product list items have tags exposed:
//    <div class="product-card" data-tags="{{ product.tags | join: ',' }}" data-product-id="{{ product.id }}"
//         data-zones="{{ product.metafields.custom.delivery_zones | default: '' }}"></div>
// 4) On product page root element (e.g., #ProductInfo), add:
//    id="ProductInfo" data-tags="{{ product.tags | join: ',' }}" data-product-id="{{ product.id }}"
//    data-zones="{{ product.metafields.custom.delivery_zones | default: '' }}"
// 5) In cart item element, add:
//    class="cart-item" data-tags="{{ item.product.tags | join: ',' }}" data-product-id="{{ item.product.id }}"
//    data-zones="{{ item.product.metafields.custom.delivery_zones | default: '' }}"

(function () {
  'use strict';

  document.addEventListener('DOMContentLoaded', () => {
    init();
  });

  // --- Constants / Config ---
  const STORAGE_KEY = 'customer_delivery_tag';
  const STORAGE_HUMAN_KEY = 'customer_delivery_location'; // for display: province + district
  const INJECTED = (window && window.DELIVERY_ASSET_URLS) || {};
  const LOCATIONS_URL_PRIMARY = INJECTED.turkeyLocations || getAssetUrl('Turkey.json');
  const LOCATIONS_URL_FALLBACK = INJECTED.turkeyFallback || '';
  const ZONES_URL = INJECTED.zones || getAssetUrl('delivery-zones.json');

  // --- Helpers ---
  function getAssetUrl(filename) {
    // Shopify serves assets via {{ 'file' | asset_url }}; when referenced directly in JS,
    // we expect themes to be at root /, so try /assets/<file>. If your CDN rewrites, adjust accordingly.
    return `/assets/${filename}`;
  }

  function slugifyTr(input) {
    if (!input) return '';
    const map = {
      'Ç': 'c', 'ç': 'c',
      'Ğ': 'g', 'ğ': 'g',
      'İ': 'i', 'I': 'i', 'ı': 'i',
      'Ö': 'o', 'ö': 'o',
      'Ş': 's', 'ş': 's',
      'Ü': 'u', 'ü': 'u'
    };
    return String(input)
      .trim()
      .replace(/[ÇçĞğİIıÖöŞşÜü]/g, m => map[m] || m)
      .normalize('NFKD')
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .toLowerCase();
  }

  function buildDeliveryTag(provinceName, districtName) {
    return `delivery-${slugifyTr(provinceName)}-${slugifyTr(districtName)}`;
  }

  function readJsonSafe(url) {
    return fetch(url, { credentials: 'same-origin' }).then(r => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    });
  }

  function ensureModalVisible() {
    const overlay = document.getElementById('location-modal-overlay');
    if (overlay) {
      overlay.style.display = 'block';
      overlay.setAttribute('aria-hidden', 'false');
      try {
        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';
      } catch (e) { }
    }
  }

  function hideModal() {
    const overlay = document.getElementById('location-modal-overlay');
    if (overlay) {
      overlay.style.display = 'none';
      overlay.setAttribute('aria-hidden', 'true');
      try {
        document.documentElement.style.overflow = '';
        document.body.style.overflow = '';
      } catch (e) { }
    }
  }

  function setHeaderCurrentLocationDisplay(text) {
    const el = document.getElementById('current-location-display');
    if (el) el.textContent = text || '';
  }

  // --- State ---
  let locationsData = null; // { provinces: [ { name, districts: [..] } ] } OR [ { id, name, districts: [ {name}, ..] } ]
  let zonesMap = {}; // { zone-handle: [delivery-tag, ...] }

  // --- Core ---
  let isInitialized = false;
  
  async function init() {
    if (isInitialized) return;
    isInitialized = true;
    
    // Don't run heavy operations on collection pages in customizer
    if (window.Shopify && window.Shopify.designMode && 
        (window.location.pathname.includes('/collections/') || 
         document.querySelector('.collection') || 
         document.querySelector('[data-collection]'))) {
      console.log('Location filter: Skipping collection page in design mode');
      return;
    }
    
    await loadZones();
    await loadLocations();

    const savedTag = localStorage.getItem(STORAGE_KEY);
    const savedHuman = localStorage.getItem(STORAGE_HUMAN_KEY);

    // Setup modal handlers
    setupModalHandlers();
    
    // Populate modal provinces
    populateProvinces();

    // Wire inline selectors if present
    const hasInline = wireInlineSelectors(savedTag, savedHuman);

    if (savedTag) {
      // Show in header
      setHeaderCurrentLocationDisplay(savedHuman || savedTag);
      filterCollectionProducts();
      validateProductPage();
      validateCart();
    } else {
      // No location saved, show modal
      ensureModalVisible();
    }
    
    // Add cleanup on page unload
    window.addEventListener('beforeunload', cleanup);
  }
  
  function cleanup() {
    isInitialized = false;
    // Clear any running intervals or timeouts
    if (filterCollectionProducts.isRunning) {
      filterCollectionProducts.isRunning = false;
    }
  }

  async function loadZones() {
    try {
      zonesMap = await readJsonSafe(ZONES_URL);
    } catch (e) {
      console.error('Failed to load delivery zones:', e);
      zonesMap = {};
    }
  }

  async function loadLocations() {
    // Try Turkey.json first (your provided structure)
    try {
      const raw = await readJsonSafe(LOCATIONS_URL_PRIMARY);
      if (Array.isArray(raw)) {
        locationsData = {
          provinces: raw.slice(0, 81).map(p => ({
            name: titleCaseTr(p.name),
            districts: Array.isArray(p.districts) ? p.districts.slice(0, 50).map(d => titleCaseTr(d.name)) : []
          }))
        };
        console.log('Location data loaded successfully:', locationsData.provinces.length, 'provinces');
        return;
      }
    } catch (e) {
      console.error('Failed to load Turkey.json:', e);
    }
    
    // Fallback to simplified format if available
    if (LOCATIONS_URL_FALLBACK) {
      try {
        const data = await readJsonSafe(LOCATIONS_URL_FALLBACK);
        // expect format: [ { name: 'Ankara', districts: ['Çankaya', ...] }, ... ]
        if (Array.isArray(data)) {
          locationsData = { provinces: data.map(p => ({ name: p.name, districts: p.districts })) };
          return;
        }
      } catch (e) {
        console.error('Failed to load fallback locations:', e);
      }
    }
    
    console.error('Failed to load locations data. Check that Turkey.json exists in Assets and is publicly accessible.');
    locationsData = { provinces: [] };
  }

  function titleCaseTr(str) {
    if (!str) return '';
    // Input likely already uppercase; return as proper case without losing Turkish letters
    const lower = str.toLocaleLowerCase('tr-TR');
    return lower.split(' ').map(w => w.charAt(0).toLocaleUpperCase('tr-TR') + w.slice(1)).join(' ');
  }

  function setupModalHandlers() {
    const provinceSel = document.getElementById('province-selector');
    const districtSel = document.getElementById('district-selector');
    const saveBtn = document.getElementById('location-save-btn');

    if (provinceSel) provinceSel.addEventListener('change', handleProvinceChange);
    if (saveBtn) saveBtn.addEventListener('click', saveLocation);

    // Keep modal un-dismissable: no escape or overlay click handler
    const overlay = document.getElementById('location-modal-overlay');
    if (overlay) {
      overlay.addEventListener('click', (e) => {
        // Prevent closing on overlay click
        e.stopPropagation();
      });
    }
  }

  function populateProvinces() {
    const provinceSel = document.getElementById('province-selector');
    if (!provinceSel || !locationsData || !Array.isArray(locationsData.provinces)) return;

    // Reset
    provinceSel.innerHTML = '<option value="">İl seçiniz</option>';

    // Sort provinces with Tekirdağ first, then alphabetically
    const sortedProvinces = locationsData.provinces
      .slice()
      .sort((a, b) => {
        // Tekirdağ always first
        if (a.name === 'Tekirdağ') return -1;
        if (b.name === 'Tekirdağ') return 1;
        // Then alphabetical
        return a.name.localeCompare(b.name, 'tr-TR');
      });

    sortedProvinces.forEach(p => {
      const opt = document.createElement('option');
      opt.value = p.name;
      opt.textContent = p.name;
      provinceSel.appendChild(opt);
    });

    // District select reset
    const districtSel = document.getElementById('district-selector');
    if (districtSel) {
      districtSel.innerHTML = '<option value="">Önce il seçiniz</option>';
      districtSel.disabled = true;
    }
  }

  function handleProvinceChange() {
    const provinceSel = document.getElementById('province-selector');
    const districtSel = document.getElementById('district-selector');
    if (!provinceSel || !districtSel) return;

    const selectedProvince = provinceSel.value;
    const province = (locationsData?.provinces || []).find(p => p.name === selectedProvince);

    districtSel.innerHTML = '';
    if (!province) {
      districtSel.innerHTML = '<option value="">Önce il seçiniz</option>';
      districtSel.disabled = true;
      return;
    }

    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = 'İlçe seçiniz';
    districtSel.appendChild(placeholder);

    province.districts
      .slice()
      .sort((a, b) => a.localeCompare(b, 'tr-TR'))
      .forEach(d => {
        const opt = document.createElement('option');
        opt.value = d;
        opt.textContent = d;
        districtSel.appendChild(opt);
      });

    districtSel.disabled = false;
  }

  function saveLocation() {
    const provinceSel = document.getElementById('province-selector');
    const districtSel = document.getElementById('district-selector');
    const feedback = document.getElementById('location-feedback');

    const province = provinceSel ? provinceSel.value : '';
    const district = districtSel ? districtSel.value : '';

    if (!province || !district) {
      if (feedback) {
        feedback.style.color = '#d00';
        feedback.textContent = 'Lütfen il ve ilçe seçiniz.';
      }
      return;
    }

    const tag = buildDeliveryTag(province, district);
    localStorage.setItem(STORAGE_KEY, tag);
    localStorage.setItem(STORAGE_HUMAN_KEY, `${province} / ${district}`);

    // Dispatch location change event for cart validator
    const locationChangeEvent = new CustomEvent('location:changed', {
      detail: {
        province: province,
        district: district,
        tag: tag,
        humanReadable: `${province} / ${district}`
      }
    });
    document.dispatchEvent(locationChangeEvent);

    if (feedback) {
      feedback.style.color = '#2e7d32';
      feedback.textContent = 'Kaydedildi. Sayfa yenileniyor...';
    }

    hideModal();
    window.location.reload();
  }

  // --- Inline selector support ---
  function getProvinceByName(name) {
    return (locationsData?.provinces || []).find(p => p.name === name);
  }

  function wireInlineSelectors(savedTag, savedHuman) {
    const containers = document.querySelectorAll('[data-location-selector]');
    if (!containers || containers.length === 0) return false;

    // Initialize each container
    containers.forEach(container => {
      const provinceSel = container.querySelector('.ls-province');
      const districtSel = container.querySelector('.ls-district');
      const saveBtn = container.querySelector('.ls-save');
      const feedback = container.querySelector('.ls-feedback');
      const current = container.querySelector('.ls-current');

      // Populate provinces
      if (provinceSel) {
        provinceSel.innerHTML = '<option value=\"\">İl seçiniz</option>';
        (locationsData?.provinces || [])
          .slice()
          .sort((a, b) => {
            // Tekirdağ always first
            if (a.name === 'Tekirdağ') return -1;
            if (b.name === 'Tekirdağ') return 1;
            // Then alphabetical
            return a.name.localeCompare(b.name, 'tr-TR');
          })
          .forEach(p => {
            const opt = document.createElement('option');
            opt.value = p.name; opt.textContent = p.name;
            provinceSel.appendChild(opt);
          });
      }

      // Province change handler for this container
      if (provinceSel && districtSel) {
        districtSel.innerHTML = '<option value=\"\">Önce il seçiniz</option>';
        districtSel.disabled = true;
        provinceSel.addEventListener('change', () => {
          const selectedProvince = provinceSel.value;
          const province = getProvinceByName(selectedProvince);
          districtSel.innerHTML = '';
          if (!province) {
            districtSel.innerHTML = '<option value=\"\">Önce il seçiniz</option>';
            districtSel.disabled = true;
            return;
          }
          const placeholder = document.createElement('option');
          placeholder.value = '';
          placeholder.textContent = 'İlçe seçiniz';
          districtSel.appendChild(placeholder);
          province.districts.slice().sort((a, b) => a.localeCompare(b, 'tr-TR')).forEach(d => {
            const opt = document.createElement('option');
            opt.value = d; opt.textContent = d;
            districtSel.appendChild(opt);
          });
          districtSel.disabled = false;
        });
      }

      // Save button handler
      if (saveBtn) {
        saveBtn.addEventListener('click', () => {
          const province = provinceSel ? provinceSel.value : '';
          const district = districtSel ? districtSel.value : '';
          if (!province || !district) {
            if (feedback) { feedback.style.color = '#d00'; feedback.textContent = 'Lütfen il ve ilçe seçiniz.'; }
            return;
          }
          const tag = buildDeliveryTag(province, district);
          localStorage.setItem(STORAGE_KEY, tag);
          localStorage.setItem(STORAGE_HUMAN_KEY, `${province} / ${district}`);

          // Warning clearing removed - handled by inline selector

          // Dispatch location change event for cart validator
          const locationChangeEvent = new CustomEvent('location:changed', {
            detail: {
              province: province,
              district: district,
              tag: tag,
              humanReadable: `${province} / ${district}`
            }
          });
          document.dispatchEvent(locationChangeEvent);

          if (feedback) { feedback.style.color = '#2e7d32'; feedback.textContent = 'Kaydedildi.'; }
          if (current) current.textContent = `${province} / ${district}`;
          // Re-apply filters immediately without full reload to keep UX smooth
          filterCollectionProducts();
          validateProductPage();
          validateCart();
        });
      }

      // Initialize current text and preselect if saved
      if (current) current.textContent = savedHuman || '';
      if (savedTag && provinceSel && districtSel) {
        // Try to preselect based on saved human-readable if available
        if (savedHuman && savedHuman.includes('/')) {
          const [prov, dist] = savedHuman.split('/').map(s => s.trim());
          if (prov && dist) {
            // Set province
            provinceSel.value = prov;
            provinceSel.dispatchEvent(new Event('change'));
            // Defer set district a tick after options are populated
            setTimeout(() => {
              districtSel.value = dist;
            }, 0);
          }
        }
      }
    });

    return true;
  }

  // --- Filtering & Validation ---
  function getZonesFromAttributes(el) {
    // Returns an array of zone handles from tags and/or metafields
    const tagsAttr = (el.getAttribute('data-tags') || '').split(',').map(t => t.trim()).filter(Boolean);
    const zonesFromTags = tagsAttr.filter(t => t.toLowerCase().startsWith('zone-'));

    const zonesMetaAttr = el.getAttribute('data-zones') || '';
    const zonesFromMeta = zonesMetaAttr
      ? zonesMetaAttr.split(',').map(z => z.trim()).filter(Boolean)
      : [];

    // Merge unique
    const set = new Set([...zonesFromTags, ...zonesFromMeta]);
    return Array.from(set);
  }

  function isProductAvailableForCustomer(el, customerTag) {
    // If no zones => nationwide => available
    const zones = getZonesFromAttributes(el);
    if (zones.length === 0) return true;

    // If zones exist, product is available if any zone array includes the customer tag
    for (const zone of zones) {
      const list = zonesMap[zone];
      if (Array.isArray(list) && list.includes(customerTag)) {
        return true;
      }
    }
    return false;
  }

  function filterCollectionProducts() {
    const customerTag = localStorage.getItem(STORAGE_KEY);
    if (!customerTag) return;
    
    // Prevent multiple simultaneous executions
    if (filterCollectionProducts.isRunning) return;
    filterCollectionProducts.isRunning = true;
    
    try {
      // Use requestAnimationFrame to avoid blocking the main thread
      requestAnimationFrame(() => {
        try {
          // Prefer hiding the entire list item so the grid reflows without gaps
          const items = document.querySelectorAll('.product-grid__item');
          if (items && items.length) {
            items.forEach(item => {
              const probe = item.querySelector('.product-card') || item;
              const visible = isProductAvailableForCustomer(probe, customerTag);
              item.style.display = visible ? '' : 'none';
            });
            return;
          }
          
          // Fallbacks: homepage product list and generic product cards
          const listItems = document.querySelectorAll('.section-resource-list .resource-list__item');
          if (listItems && listItems.length) {
            listItems.forEach(wrapper => {
              const probe = wrapper.querySelector('.product-card') || wrapper;
              const visible = isProductAvailableForCustomer(probe, customerTag);
              wrapper.style.display = visible ? '' : 'none';
            });
            return;
          }

          // Custom collection products grid (Lilyum): filter articles with class .collection-product-card
          const collectionCards = document.querySelectorAll('.collection-products-grid .collection-product-card');
          if (collectionCards && collectionCards.length) {
            collectionCards.forEach(card => {
              const visible = isProductAvailableForCustomer(card, customerTag);
              card.style.display = visible ? '' : 'none';
            });
            return;
          }

          // Mobile Product Showcase (Lilyum custom): filter .mps-card inside .mps__grid
          const showcaseCards = document.querySelectorAll('.mps .mps__grid .mps-card');
          if (showcaseCards && showcaseCards.length) {
            showcaseCards.forEach(card => {
              // Prefer reading data from the card itself; otherwise, search for any child carrying data attributes
              let probe = card;
              if (!probe.getAttribute('data-tags') && !probe.getAttribute('data-zones')) {
                const nested = card.querySelector('[data-tags],[data-zones]');
                if (nested) probe = nested;
              }
              const visible = isProductAvailableForCustomer(probe, customerTag);
              card.style.display = visible ? '' : 'none';
            });
            return;
          }
          
          // Last fallback: hide product-card or its closest li
          const cards = document.querySelectorAll('.product-card');
          cards.forEach(card => {
            const visible = isProductAvailableForCustomer(card, customerTag);
            const li = card.closest('li');
            if (li) {
              li.style.display = visible ? '' : 'none';
            } else {
              card.style.display = visible ? '' : 'none';
            }
          });
        } finally {
          filterCollectionProducts.isRunning = false;
        }
      });
    } catch (error) {
      console.error('Error in filterCollectionProducts:', error);
      filterCollectionProducts.isRunning = false;
    }
  }

  function validateProductPage() {
    const customerTag = localStorage.getItem(STORAGE_KEY);
    if (!customerTag) return;

    const info = document.getElementById('ProductInfo') || document.querySelector('[data-product-id]');
    if (!info) return;

    const available = isProductAvailableForCustomer(info, customerTag);
    const btn = document.querySelector('#AddToCart, [name="add"], button[name="add"]');
    const notice = ensureProductNoticeElement();

    if (!available) {
      if (btn) {
        btn.setAttribute('disabled', 'disabled');
        btn.style.opacity = '0.6';
        btn.style.cursor = 'not-allowed';
      }
      if (notice) {
        notice.textContent = 'Seçtiğiniz bölge için bu ürün teslimata uygun değildir.';
        notice.style.display = 'block';
        notice.style.opacity = '1';
      }
    } else {
      if (btn) {
        btn.removeAttribute('disabled');
        btn.style.opacity = '';
        btn.style.cursor = '';
      }
      if (notice) {
        notice.textContent = '';
        notice.style.display = 'none';
        notice.style.opacity = '0';
      }
    }
  }

  function ensureProductNoticeElement() {
    // Prefer an existing location-warning element (inline selector).
    const existingWarning = document.getElementById('location-warning');
    if (existingWarning) return existingWarning;

    // Only create a product-specific notice when we're on a product page
    // (element with id ProductInfo). Do NOT inject a global banner into document.body
    // because that may create unwanted notices at the top of non-product pages.
    const productRoot = document.getElementById('ProductInfo');
    if (!productRoot) return null;

    let el = document.getElementById('product-availability-notice');
    if (!el) {
      el = document.createElement('div');
      el.id = 'product-availability-notice';
      // Pre-allocate space to prevent layout shift
      el.style.cssText = 'margin:12px 0;padding:12px;border-radius:8px;background:#fff5f5;color:#b00020;font-weight:600;display:none;min-height:44px;opacity:0;transition:opacity 0.2s ease;';
      productRoot.insertBefore(el, productRoot.firstChild);
    }
    return el;
  }

  function validateCart() {
    const customerTag = localStorage.getItem(STORAGE_KEY);

    const items = document.querySelectorAll('.cart-item');
    const checkoutBtn = document.querySelector('[name="checkout"], button[name="checkout"], #checkout');

    // Warning clearing removed - handled by inline selector

    // If there are no items, return early
    if (!items || items.length === 0) {
      return;
    }

    // Don't show any warning messages - user doesn't want them
    // Just ensure checkout button works
    if (checkoutBtn) {
      checkoutBtn.removeAttribute('disabled');
      checkoutBtn.style.opacity = '';
      checkoutBtn.style.cursor = '';
    }
  }

  function ensureCartNotice() {
    // Kırmızı uyarı mesajı devre dışı bırakıldı
    let el = document.getElementById('cart-availability-notice');
    if (el) {
      el.style.display = 'none'; // Varsa gizle
    }
    return el;
  }

  // Expose functions (optional debugging)
  window.DeliveryZones = {
    init,
    populateProvinces,
    handleProvinceChange,
    saveLocation,
    filterCollectionProducts,
    validateProductPage,
    validateCart
  };
})();
